import React, { useState } from "react";
import { Box, Menu, MenuItem } from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { useSettings } from "../../context-providers/settings-provider";
import IntentButton, { Intent } from "../input/intent-button";
import { HOME_ROLES, useHome } from "../../context-providers/home-provider";
import ConfirmDeleteDialog from "../dialog/confirm-delete-dialog";
import RemoveHomeDialog from "./remove-home-dialog";
import { useAuth } from "../../context-providers/auth-provider";
import { useInvitations } from "../../context-providers/invitations-provider";
import HomeInvitation from "./home-invitation";
import { useFriendships } from "../../context-providers/friendships-provider";
import HomeMember from "./home-member";
import HomeMemberDialog from "./home-member-dialog";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },

  emptyText: {
    color: theme.palette.grey.A200,
    padding: "10px",
  },

  loading: {
    height: "150px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  subHeading: {
    color: theme.palette.grey.A400,
    fontWeight: 600,
  },

  roleIcon: {
    height: "40px",
    width: "40px",
    marginRight: "5px",
  },

  roleName: {
    color: theme.palette.grey.A200,
  },

  deleteButton: {
    padding: "5px 4px",
    color: theme.palette.error.main,
  },

  list: {
    width: "100%",
  },

  buttonRow: {
    display: "flex",
    margin: "10px auto",
  },

  exit: {
    maxHeight: "60px",
    overflow: "hidden",
  },

  exitActive: {
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    transition: "all 500ms",
    overflow: "hidden",
  },

  enter: {
    maxHeight: 0,
    transition: "none",
    paddingTop: 0,
    paddingBottom: 0,
    overflow: "hidden",
  },

  enterActive: {
    maxHeight: "60px",
    paddingTop: "8px",
    paddingBottom: "8px",
    transition: "all 500ms",
    overflow: "hidden",
  },
}));

export default function HomeSettings() {
  const { classes } = useStyles();
  const { currentUser } = useAuth();
  const { privateSettings } = useSettings();
  const {
    home,
    addHome,
    removeHome,
    addMember,
    updateMemberRole,
    removeMember,
    acceptHomeInvitation,
    rejectHomeInvitation,
  } = useHome();
  const { homeInvitations } = useInvitations();
  const { friends, getFriend } = useFriendships();
  const [isLoading, setIsLoading] = useState(false);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDeleteMemberDialog, setShowDeleteMemberDialog] = useState(false);
  const [showMemberDialog, setShowMemberDialog] = useState(false);
  const [showDeleteHomeDialog, setShowDeleteHomeDialog] = useState(false);

  function onCreateHome() {
    addHome().finally(() => setIsLoading(false));
  }

  function onMemberClicked(event: any, member: any) {
    setSelectedMember(member);
    setOptionsAnchorEl(event.currentTarget);
    event.preventDefault();
    event.stopPropagation();
  }

  function onRemoveMember(memberId: any) {
    setOptionsAnchorEl(null);
    removeMember(memberId);
  }

  function onRemoveHome() {
    setOptionsAnchorEl(null);
    removeHome();
  }

  function onSaveMember(member: any, roleId: any) {
    setOptionsAnchorEl(null);
    if (selectedMember) {
      updateMemberRole(member, roleId);
    } else {
      addMember(member, roleId);
    }
  }

  function getMyMembership() {
    return home.members.find((member: any) => member.id === currentUser.uid);
  }

  if (!privateSettings.homeId) {
    if (!homeInvitations || !friends)
      return (
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      );
    if (homeInvitations.length) {
      const invite = {
        ...homeInvitations[0],
        sender: getFriend(homeInvitations[0].senderId),
      };
      return (
        <Box className={classes.root}>
          <HomeInvitation
            key={invite.id}
            invite={invite}
            acceptInvite={acceptHomeInvitation}
            rejectInvite={rejectHomeInvitation}
          />
        </Box>
      );
    }
    return (
      <Box className={classes.root}>
        <Typography
          className={classes.emptyText}
          variant="body1"
          align="center"
        >
          You are not part of a home
        </Typography>
        <IntentButton
          onClick={() => onCreateHome()}
          intent={Intent.PRIMARY}
          startIcon={<AddIcon />}
          variant="contained"
        >
          {isLoading ? (
            <CircularProgress size={25} color="inherit" />
          ) : (
            "Create Home"
          )}
        </IntentButton>
      </Box>
    );
  }

  if (!home || !home.members) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  const myRole = getMyMembership().role;
  const confirmedMembers: any = [];
  const unconfirmedMembers: any = [];
  home.members.forEach((member: any) => {
    if (member.confirmed) {
      confirmedMembers.push(member);
    } else {
      unconfirmedMembers.push(member);
    }
  });

  function getMemberList(members: any) {
    return (
      <List className={classes.list}>
        <TransitionGroup>
          {members.map((member: any) => (
            <CSSTransition
              key={member.id}
              timeout={500}
              classNames={{
                enter: classes.enter,
                enterActive: classes.enterActive,
                exit: classes.exit,
                exitActive: classes.exitActive,
              }}
            >
              <HomeMember
                member={member}
                onMemberClicked={
                  member.id === currentUser.uid ||
                  (myRole && myRole.id === HOME_ROLES.HEAD_CHEF.id)
                    ? onMemberClicked
                    : null
                }
              />
            </CSSTransition>
          ))}
        </TransitionGroup>
      </List>
    );
  }

  return (
    <Box className={classes.root}>
      <Typography className={classes.subHeading} variant="body1" align="center">
        Members
      </Typography>
      {getMemberList(confirmedMembers)}
      {!!unconfirmedMembers.length && (
        <>
          <Typography
            className={classes.subHeading}
            variant="body1"
            align="center"
          >
            Invited
          </Typography>
          {getMemberList(unconfirmedMembers)}
        </>
      )}
      <Menu
        anchorEl={optionsAnchorEl}
        open={!!optionsAnchorEl}
        onClose={() => setOptionsAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        'never'.
        {selectedMember &&
          selectedMember.id !== currentUser.uid &&
          myRole &&
          myRole.id === HOME_ROLES.HEAD_CHEF.id && (
            <MenuItem
              onClick={() => {
                setShowMemberDialog(true);
                setOptionsAnchorEl(null);
              }}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="medium" />
              </ListItemIcon>
              Change Role
            </MenuItem>
          )}
        <MenuItem
          onClick={() => {
            setShowDeleteMemberDialog(true);
            setOptionsAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="medium" />
          </ListItemIcon>
          'never'.
          {selectedMember && selectedMember.id === currentUser.uid
            ? "Leave Home"
            : "Remove Member"}
        </MenuItem>
      </Menu>
      <ConfirmDeleteDialog
        open={showDeleteMemberDialog}
        handleClose={() => {
          setOptionsAnchorEl(null);
          setShowDeleteMemberDialog(false);
        }}
        item={selectedMember}
        onDelete={onRemoveMember}
        idProp="id"
        nameProp="username"
        itemType="Home Member"
        title={
          selectedMember && selectedMember.id === currentUser.uid
            ? "Confirm Leave Home"
            : null
        }
        text={
          selectedMember && selectedMember.id === currentUser.uid
            ? "Are you sure you want to leave this home?"
            : null
        }
        buttonText={
          selectedMember && selectedMember.id === currentUser.uid
            ? "Leave"
            : "Remove"
        }
      />
      {myRole.id === HOME_ROLES.HEAD_CHEF.id && (
        <Box className={classes.buttonRow}>
          <IntentButton
            onClick={() => {
              setSelectedMember(null);
              setOptionsAnchorEl(null);
              setShowMemberDialog(true);
            }}
            intent={Intent.PRIMARY}
            startIcon={<PersonAddIcon />}
            variant="contained"
          >
            Add Member
          </IntentButton>
          <IntentButton
            onClick={() => {
              setOptionsAnchorEl(null);
              setShowDeleteHomeDialog(true);
            }}
            intent={Intent.ERROR}
            startIcon={<DeleteIcon />}
            variant="contained"
          >
            Delete Home
          </IntentButton>
        </Box>
      )}
      <RemoveHomeDialog
        open={showDeleteHomeDialog}
        handleClose={() => {
          setOptionsAnchorEl(null);
          setShowDeleteHomeDialog(false);
        }}
        onDelete={onRemoveHome}
      />
      <HomeMemberDialog
        open={showMemberDialog}
        member={selectedMember}
        handleClose={() => {
          setOptionsAnchorEl(null);
          setShowMemberDialog(false);
        }}
        saveMember={onSaveMember}
      />
      <br />
    </Box>
  );
}
